import Luminary from '../index';
import type { OpaqueVideoData } from '../video-data/types';
import { Config } from '../video-data/config';

export type SlideRange = {
    start: number;
    end: number;
};

type SliceResult = {
    slideRange: SlideRange;
    startTime: number;
    endTime: number;
};

function isWithinTolerance(value, target, tolerance) {
    return Math.abs(value - target) <= tolerance;
}

/**
 * VideoDataSlicer
 */
export class VideoDataSlice {
    #startTimeMS = 0;
    #endTimeMS = 1;
    #fps: number;
    dataLuminary: Luminary;

    private constructor(videoData: OpaqueVideoData, startTime: number, endTime: number, fps: number) {
        this.#startTimeMS = startTime;
        this.#endTimeMS = endTime;
        this.#fps = fps;
        this.dataLuminary = new Luminary({
            fetchResources: false,
            isCloudBrowser: true,
            disableRenderer: true,
        });
        this.dataLuminary.load(videoData);

        // Our video renderer doesn't support scene groups with no scenes.
        if (this.dataLuminary.sceneGroups.some((sceneGroup: any) => sceneGroup.scenes.length === 0)) {
            new Error('Video contains scene groups with no scenes');
        }
    }

    slice(): SlideRange {
        console.log('startTime', this.#startTimeMS);
        console.log('endTime', this.#endTimeMS);
        const numScenes = this.dataLuminary.scenes.length;
        console.log('numScenes', numScenes);
        const duration = this.dataLuminary.duration;

        if (this.#startTimeMS > duration) {
            throw Error(`startTimeMS has a value of ${this.#startTimeMS} which is larger than duration of ${duration}. Unable to continue`);
        }

        console.log({ duration });
        if (this.#endTimeMS < 0 || this.#endTimeMS > duration || isWithinTolerance(this.#endTimeMS, duration, Config.VIDEO_TIME_PRECISION)) {
            this.#endTimeMS = duration;
        }

        // This is a hack that we probably should fix later.
        // Currently, we have issues both with the captions timing and the TH media video
        // timing when slicing scenes. This is because the duration of the scene group changes
        // as we remove scenes and the caption animations are relative to the sceneGroup,
        // same for the scene group media (voiceover/talking head) that we try to seek.
        let firstScene = this.dataLuminary.getActiveScenesAt(this.#startTimeMS)[0];
        let timeOffsetFromSlicing = firstScene.startTime;
        if (firstScene.sceneGroup.mediaAsset || firstScene.sceneGroup.voiceoverAsset) {
            firstScene = firstScene.sceneGroup.firstScene();
            timeOffsetFromSlicing = firstScene.startTime;
            console.log(`first scene is TH, starting from ${firstScene.globalIndex}`);
        }

        // end
        let lastSlideIndex: number;
        if (this.#endTimeMS === duration) {
            lastSlideIndex = numScenes - 1;
        }
        else {
            const activeScenes = this.dataLuminary.getActiveScenesAt(this.#endTimeMS);
            lastSlideIndex = activeScenes[activeScenes.length - 1].globalIndex;
        }

        // make sure the last slide index isn't part of a scene group, otherwise it will change the duration
        // which will change the timing of the captions and the TH media
        const lastScene = this.dataLuminary.scenes[lastSlideIndex];
        if (lastScene.sceneGroup.mediaAsset || lastScene.sceneGroup.voiceoverAsset) {
            lastSlideIndex = lastScene.sceneGroup.lastScene().globalIndex;
            console.log(`last scene is TH, starting from its last scene ${lastSlideIndex}`);
        }

        // Update start and stop seconds to reflect the global time shift after slicing
        this.#startTimeMS = this.#startTimeMS - timeOffsetFromSlicing;
        this.#endTimeMS = this.#endTimeMS - timeOffsetFromSlicing;

        // Round times up to nearest frame boundary
        const cap = duration - timeOffsetFromSlicing;
        this.#startTimeMS = this._roundUpToFrameBoundary(this.#startTimeMS, cap);
        this.#endTimeMS = this._roundUpToFrameBoundary(this.#endTimeMS, cap);

        return { start: firstScene.index, end: lastSlideIndex };
    }

    _roundUpToFrameBoundary(timeMS: number, maxMS: number) {
        const numFrames = Math.ceil(timeMS / 1000 * this.#fps);
        return Math.min(numFrames / this.#fps * 1000, maxMS);
    }

    get startTime(): number {
        return this.#startTimeMS;
    }

    get endTime(): number {
        return this.#endTimeMS;
    }

    static slice(videoData: OpaqueVideoData, startTime: number, endTime: number, fps: number): SliceResult {
        const slicer = new VideoDataSlice(videoData, startTime, endTime, fps);
        const range = slicer.slice();

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        slicer.destroy();

        return {
            slideRange: range,
            startTime: slicer.startTime,
            endTime: slicer.endTime,
        };
    }

    async destroy() {
        await this.dataLuminary.destroy();
    }
}
